/**
 * Cover page as an ordered draw list for PDFKit.
 * Coordinates are A4 points (origin top-left), matching the current cover generator.
 * Saved with PUT /report/:reportId/cover into report_cover.instructions.
 */

export const coverElementImageSrc = (element, logos = [], brandLogo = "") => {
	if (!element || element.type !== "image") return "";
	if (element.binding === "brandLogo") return brandLogo;
	return logos.find((logo) => logo.name === element.name)?.thumbnail || "";
};

export const A4 = { width: 595.28, height: 841.89 };

const signatureElement = () => ({
	id: "signature",
	type: "text",
	binding: "signature",
	x: 10,
	y: A4.height - 30,
	width: 200,
	height: 20,
	font: "Times-Roman",
	size: 11,
	align: "left",
});

const PAGE_SIZES = { A4 };

/** Point size for a page. Saved JSON only stores the name, e.g. "A4". */
export const pageMetrics = (page) => PAGE_SIZES[page?.size] || A4;

export const createDefaultCoverInstructions = ({ reportName = "", coverImgName = "" } = {}) => ({
	version: 2,
	page: { size: "A4" },
	elements: [
		{
			id: "brand-logo",
			type: "image",
			binding: "brandLogo",
			x: 0,
			y: 75,
			width: A4.width,
			height: 70,
			align: "center",
			valign: "middle",
		},
		{
			id: "title",
			type: "text",
			binding: "reportName",
			text: reportName,
			x: 0,
			y: 300,
			width: A4.width,
			height: 48,
			font: "Times-Bold",
			size: 35,
			align: "center",
		},
		{
			id: "cover-image",
			type: "image",
			binding: "coverImage",
			name: coverImgName || "",
			x: 0,
			y: 350,
			width: A4.width,
			height: Math.round(A4.height * 0.2),
			align: "center",
			valign: "middle",
		},
		{
			id: "created-date",
			type: "text",
			binding: "createdDate",
			text: "{{createdDate}}",
			x: 0,
			y: A4.height - 28,
			width: A4.width - 10,
			height: 16,
			font: "Times-Roman",
			size: 11,
			align: "right",
		},
		signatureElement(),
	],
});

const LOCKED_BINDINGS = new Set(["brandLogo", "reportName", "coverImage", "createdDate", "signature"]);

/** Logo, title, cover image, date, and signature stay on every cover. */
export const isLockedCoverElement = (element) => LOCKED_BINDINGS.has(element?.binding);

export const ensureCoverInstructions = (payload) => {
	const defaults = createDefaultCoverInstructions({
		reportName: payload?.reportName,
		coverImgName: payload?.coverImgName,
	});
	if (!payload?.coverInstructions?.elements?.length) {
		return { ...payload, coverInstructions: defaults };
	}
	const instructions = payload.coverInstructions;
	const present = new Set(instructions.elements.map((element) => element.binding).filter(Boolean));
	const missing = defaults.elements.filter((element) => element.binding && !present.has(element.binding));
	if (missing.length === 0) return payload;
	return {
		...payload,
		coverInstructions: {
			...instructions,
			version: 2,
			elements: [...instructions.elements, ...missing],
		},
	};
};

export const elementAlign = (element) => element?.align || (element?.type === "image" ? "center" : "left");

export const elementValign = (element) => element?.valign || (element?.type === "image" ? "middle" : "top");

/** CSS object-position. "middle" is the cover value; CSS uses "center". */
export const cssObjectPosition = (element) => {
	const vertical = elementValign(element) === "middle" ? "center" : elementValign(element);
	return `${elementAlign(element)} ${vertical}`;
};

export const elementLabel = (element) => {
	const custom = String(element?.label || "").trim();
	if (custom) return custom;
	if (element.binding === "brandLogo") return "Company logo";
	if (element.binding === "reportName") return "Title";
	if (element.binding === "coverImage") return "Cover image";
	if (element.binding === "createdDate") return "Created date";
	if (element.binding === "signature") return "Signature";
	return element.type === "image" ? "Image" : "Text";
};

export const displayText = (element, { reportName = "", dateString = "" } = {}) => {
	if (element.binding === "signature") return "Signature";
	if (element.binding === "createdDate" || element.text === "{{createdDate}}") return dateString;
	if (element.binding === "reportName") return element.text || reportName;
	return element.text || "";
};

/** Fields the existing report row already stores, derived from bound elements. */
export const reportFieldsFromInstructions = (instructions, fallback = {}) => {
	const elements = instructions?.elements || [];
	const title = elements.find((element) => element.binding === "reportName");
	const image = elements.find((element) => element.binding === "coverImage");
	const reportName = title ? String(title.text || "").trim() : fallback.reportName;
	const coverImgName = image ? image.name || "" : fallback.coverImgName;
	return {
		reportName: reportName || fallback.reportName || "",
		coverImgName: coverImgName ?? fallback.coverImgName ?? "",
	};
};

export const createTextElement = () => ({
	id: `text-${Date.now()}`,
	type: "text",
	text: "New text",
	x: 48,
	y: 460,
		width: 240,
		height: 24,
		font: "Times-Roman",
		size: 16,
		align: "left",
		valign: "top",
});

export const createImageElement = () => ({
	id: `image-${Date.now()}`,
	type: "image",
	name: "",
	x: 48,
	y: 500,
	width: 240,
	height: 120,
	align: "center",
	valign: "middle",
});
